import { type AuthId, type CalendarId, EventId } from "@tento-lona/sheets";
import type { Option } from "@tento-lona/sheets";
import { DateTime, MsSinceEpoch, NaiveDate, SemanticTime, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { AVLTree } from "@tento-core/avl-tree";
import type { DebouncedRequests } from "@tento-core/debounced-requests";
import { Tracing } from "@tento-core/tracing";
import type { TypedApi } from "@tento-core/typed-api";
import { type DurableHandoffs, DurableRegistries } from "../client/durable-handoffs.ts";
import { EventIdPromotions } from "./event-id-promotions.ts";
import type {
  CellEntry,
  CellStyle,
  CellValue,
  TimepointCellEntry,
  CalendarEvent,
} from "@tento-lona/sheets";
import { CellId, EventOrigin, Json, RowLocalId, RowNetworkKey } from "@tento-lona/sheets";
import { RecurrenceInfo } from "@tento-lona/sheets/platform";
import { CellLane } from "@tento-lona/sheets/internal-sdk";
import { TimeMembership } from "@tento-lona/sheets/internal-sdk";
import { isCanonicalKey } from "@tento-lona/sheets/platform";
import { eventDbToEvent, WireDecode } from "@tento-lona/sheets";
import { EventItem, EventItemAllDay, EventItemTimed } from "../events/event-item.ts";
// Type-only: a runtime import here would pull the mutation graph
// (event-mutations → event-item → @tento/lona/sheets) into this module's
// eager init, and `events-table` sits in the `src ↔ sheets` import cycle — the
// re-entry reads `@tento/lona/sheets` mid-init (e.g. `DATA_GRAPH_ROW_HEIGHTS`
// resolves wrong). `EventOps` (which already imports the mutation graph)
// constructs the projector via `ensureOverlayProjector`.
import type { EventOverlayProjector } from "../mutation/event-overlay-projector.ts";
import type { ProjectionWrite } from "@tento-lona/sheets/mutation";
import { AuthScopedCalendarId, type CalendarLookup } from "../events/calendar-types.ts";
import { SdkRequestError } from "../errors.ts";
import {
  Actor,
  InvalidationSink,
  TimepointsChanged,
  ViewportsInvalidated,
} from "../invalidation.ts";
import { Network } from "../network.ts";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import { TaggedCells } from "../cells/tagged-cells.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

/**
 * Per-row state held by `EventsTable` for every event-bearing
 * `RowCellCache` it tracks. Carries the primitives `EventOps`
 * reaches for (api / invalidation are on the table; rh /
 * rowLocalId / rowKey come from here) plus the recurrence
 * machinery + the meta-row aggregator loader.
 *
 * Phase E.0g: replaces `EventsRowBinding` — what was a per-row
 * class is now a record owned by the table.
 */
export interface EventsRowState {
  readonly rowLocalId: RowLocalId;
  readonly rowKey: RowNetworkKey;
  readonly rh: RowCellCache;
  /** Set when this is an aggregator row (calendar / calendar-list);
   *  triggers fetch fan-out across constituent source rows. */
  metaSourcesLoader: Option<(range: Option<DateTime.Range<Utc>>) => Promise<void>>;
}


/**
 * Minimal write surface of the rows-engine canonical cell store that
 * `EventsTable` mirrors optimistic edits into. Structurally satisfied
 * by `SheetModelCache.canonicalCells` (`CellDataStore<string>`). The
 * trailing `style` and `actor` slots mirror the underlying store's
 * signature; pass `null` from the events path (untagged optimistic
 * mirror).
 */
export interface CanonicalCellSink {
  set(
    key: string,
    date: NaiveDate.Partial,
    value: CalendarEvent,
    cellId: Option<CellId>,
    style: Option<CellStyle>,
    actor: Option<Actor>,
  ): void;
  clear(
    key: string,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): void;
  /** Upsert one timepoint (datetime-positioned) cell — the timed-event
   *  lane. Structurally matches `CellDataStore.setCustom`. */
  setCustom(
    key: string,
    entry: CellEntry.Timepoint,
    actor: Option<Actor>,
  ): void;
  /** Remove one timepoint cell by raw id — the timed-event lane clear.
   *  Structurally matches `CellDataStore.removeCustom`. */
  removeCustom(key: string, id: string, actor: Option<Actor>): void;
}

/**
 * A read-only view of an events table: id resolution and the range / date
 * queries a renderer or a preview reads. What `seededEventsTable` returns; the
 * live, client-scoped `EventsTable` implements it. `scope` narrows a query to
 * the events of those backing row ids (`null`: every row).
 */
export interface EventsTableReads {
  /** Whether an event with `id` is indexed. */
  has(id: EventId): boolean;
  /** The indexed event for `id`; `null` when absent. */
  itemFor(id: EventId): Option<EventItem>;
  /** Timed events that belong to the UTC day of `dt` (`TimeMembership`: half-open). */
  peekOnDate(
    dt: DateTime<Utc>,
    scope: Option<ReadonlySet<string>>,
  ): readonly EventItemTimed[];
  /** Timed events that belong to `range` (`TimeMembership`: half-open, end-exclusive). */
  peekInRange(
    range: DateTime.Range<Utc>,
    scope: Option<ReadonlySet<string>>,
  ): readonly EventItemTimed[];
  /** All-day events on the days of `range` (`range.end` exclusive: the day before it is the last). */
  peekAllDayInRange(
    range: NaiveDate.Range,
    scope: Option<ReadonlySet<string>>,
  ): readonly EventItemAllDay[];
}

/** `EventsTable`'s friend record (`Internals`): its constructor registers each instance. */
type EventsTableInternals = {
  readonly emitUpdate: (table: EventsTable, prev: EventItem, next: EventItem, actor: Actor) => void;
};
const eventsTableInternals = new Internals<EventsTable, EventsTableInternals>("EventsTable");

/**
 * Fire the post-write invalidation for an event that moved from `prev` to
 * `next`, through `table`'s sink, stamped with `actor` (the write's author).
 *
 * @internal SDK-private — `EventOps` reaches the table's emitter through this
 * friend; neither it nor the `EventsTable` class is on the `@tento/lona` barrel.
 */
export function emitEventUpdate(
  table: EventsTable,
  prev: EventItem,
  next: EventItem,
  actor: Actor,
): void {
  eventsTableInternals.of(table).emitUpdate(table, prev, next, actor);
}

/**
 * Client-scoped event storage — a single set of AVL trees + Map
 * indexes covering events from **all** event-bearing rows on a
 * `LonaSdk.Client`, plus per-row binding state (replaces the
 * previously-separate `EventsRowBinding` in Phase E.0g).
 *
 * Each ingested event is tagged via `event.rowId` so callers can
 * scope queries to a single source row, a set of constituent rows
 * (aggregator views), or no scope at all (cross-client lookups).
 *
 * Single instance per `LonaSdk.Client`. The Client (via
 * `RowsClient`) calls `attachRow` for every event-bearing row it
 * opens. `EventOps.fromHandle(rh)` finds the table via the static
 * `tableByRowHandle` WeakMap, then looks up the per-row state for
 * its primitives.
 */
/**
 * Work counter of every `EventsTable` in the process, for the complexity
 * tests (nothing in the SDK reads it): events `populateRow` ingested, and
 * events a populate compared against the fresh cells (`populateVisits`), and
 * days a scoped populate's anchor lookup read (`populateLookbackDays`).
 */
export const EventsTableWork = { populateIngests: 0, populateVisits: 0, populateLookbackDays: 0 };

export class EventsTable implements EventsTableReads {
  static readonly #internals: EventsTableInternals = {
    emitUpdate: (table, prev, next, actor) =>
      table.#emitUpdate(prev, next, actor),
  };

  /** Static lookup: every `RowCellCache` attached to *some* table maps
   *  to that table here. Lets `EventOps.fromHandle(rh)` find the
   *  table without the caller needing a reference. There's exactly
   *  one EventsTable per `LonaSdk.Client`, but a process can have
   *  multiple Clients, so a WeakMap (not a singleton) is correct. */
  static readonly #tableByRowHandle = new WeakMap<RowCellCache, EventsTable>();

  /** Resolve the EventsTable that attached this `RowCellCache`. */
  static fromHandle(rh: RowCellCache): Option<EventsTable> {
    return EventsTable.#tableByRowHandle.get(rh) ?? null;
  }

  /** Origin marker for invalidations emitted from this table — lets
   *  consumers suppress redundant re-renders from their own optimistic
   *  writes. */
  static readonly ACTOR = new Actor("events-table");

  /** Canonical `Event` by id — cache of decoded wire values. */
  readonly rawById = new Map<EventId, CalendarEvent>();

  /** Hydrated partial-day items keyed by id for O(1) lookup. */
  readonly baseIndex = new Map<string, EventItemTimed>();

  /** Interval-tree of partial-day items keyed on their UTC `mse` range.
   *  Backs `peekInRange` / `peekOnDate` with O(log n + k) queries. */
  readonly rangeIndex = new AVLTree<
    AVLTree.Node.Many<string, EventItemTimed>
  >();

  /** Hydrated full-day items keyed by id for O(1) lookup. */
  readonly allDayBaseIndex = new Map<string, EventItemAllDay>();

  /** Interval-tree of full-day items keyed on their DSE range. */
  readonly allDayIndex = new AVLTree<
    AVLTree.Node.Many<string, EventItemAllDay>
  >();

  /** Reverse map for partial-clear: stable row identity → ids of events tagged
   *  with that row. */
  readonly #idsByRow = new Map<RowLocalId, Set<EventId>>();
  /** Exactly one stable backing-row owner per indexed event. */
  readonly #rowByEvent = new Map<EventId, RowLocalId>();

  /** Per-row binding state. Keyed by stable RowLocalId so multiple lookups
   *  (by rh via byRowHandle, by rowKey via aggregator loaders)
   *  agree on the same RowState instance. */
  readonly #rowStateById = new Map<RowLocalId, EventsRowState>();
  readonly #rowStateByHandle = new Map<RowCellCache, EventsRowState>();

  /** Durable (service-worker) event writes awaiting their verdict, by
   *  debounce key — see `handOffDurable`. One of the owning client's
   *  `DurableRegistries`, so `client.dispose()` fails it with the others. */
  readonly #durable: DurableHandoffs;

  /** This client's ghost-promotion re-keys (`EventOps.create` records;
   *  renderers read through `eventIdPromotionsOf`). */
  readonly promotions = new EventIdPromotions();

  readonly #maybeApi: Option<TypedApi<Network.Routes>>;
  readonly #ids: RowLocalId.Registry;
  readonly #openRowHandle: (rowKey: RowNetworkKey) => Promise<Option<RowCellCache>>;
  readonly #canonicalCells: () => CanonicalCellSink;
  readonly #calendars: () => Option<CalendarLookup>;

  constructor(
    /** `null` only for a seeded, read-only table (`EventsTable.seeded`);
     *  read through `api`. */
    maybeApi: Option<TypedApi<Network.Routes>>,
    readonly invalidation: InvalidationSink,
    /** The owning client's identity scope. Every `RowLocalId` this
     *  table mints for an unattached canonical row is interned HERE, so it is
     *  the same object the client's sheets and `RowsClient` already hold for
     *  that key — a second scope would tag the index under a row nothing else
     *  can address. Attached rows are tagged with the localId their caller
     *  passes to `attachRow`, resolved from this same registry. */
    ids: RowLocalId.Registry,
    /** Used by aggregator-row metaSourcesLoader to open constituent
     *  source-row handles. Resolves null when the caller has no handle
     *  registry (null-state demos / tests): aggregator loadRange is
     *  then a no-op. */
    openRowHandle: (rowKey: RowNetworkKey) => Promise<Option<RowCellCache>>,
    /** The client-scoped rows-engine canonical cell store. Optimistic
     *  edits on canonical rows are written here so rows-engine projection
     *  facades (e.g. the month-view per-calendar data-source rows)
     *  invalidate and re-render immediately, instead of only after a
     *  server refetch. A getter so the owner can hand it over before the
     *  store's field is read; the client's rows accessor always has one
     *  (a seeded, read-only table passes one that refuses writes). */
    canonicalCells: () => CanonicalCellSink,
    /** Lazy calendar lookup — resolves an events-row's SERVER-RENDERED
     *  lookupKey (`CalendarDisplay.eventsLookupKey`) by `(authId, calendarId)`.
     *  Lazy because the `CalendarsClient` is constructed after this table;
     *  resolves null in seeded / standalone tables (no write path
     *  exercises it). */
    calendars: () => Option<CalendarLookup>,
    /** The registry this table's durable event writes wait in — made by the
     *  owning client's `DurableRegistries.create`, which offers it every
     *  reported refusal and fails it on `dispose`. */
    durable: DurableHandoffs,
  ) {
    eventsTableInternals.register(this, EventsTable.#internals);
    this.#maybeApi = maybeApi;
    this.#ids = ids;
    this.#openRowHandle = openRowHandle;
    this.#canonicalCells = canonicalCells;
    this.#calendars = calendars;
    this.#durable = durable;
  }

  /** The typed API every event write and fetch goes through. Throws on a
   *  seeded table, which has none. */
  get api(): TypedApi<Network.Routes> {
    return nonnull(this.#maybeApi, "EventsTable.seeded is read-only: it has no API");
  }

  /** A fresh id for one durable write — see `handOffDurable`. */
  nextDurableRequestId(): string {
    return this.#durable.nextRequestId();
  }

  /**
   * Run a durable (service-worker) event write's `handoff` under debounce
   * `key` and resolve with the refusal reported for THIS write while it was
   * in flight (`reportDurableFailure`), or `null` when none was. The handoff
   * returns no response, so this is the only place its verdict can land.
   * The matching rules and the executor's half of invariants (ii)/(iii)/(iv)
   * are `DurableHandoffs.handOff`'s: a handoff whose transport gave no
   * verdict resolves with a status-`0` failure (or throws) — never with
   * `null`, which means accepted.
   */
  async handOffDurable(
    key: string,
    requestId: string,
    handoff: () => void | Promise<void>,
  ): Promise<Option<DebouncedRequests.Failure>> {
    return this.#durable.handOff(key, requestId, handoff);
  }

  /**
   * Record a refused debounced request against the durable event writes it
   * carried (`handOffDurable`; rules in `DurableHandoffs.report`). Returns
   * `true` when at least one matched — it will take its change back and
   * reject — and `false` when none did: not an event write, or one whose
   * handoff already returned. A status of `0` is an unanswered write
   * (invariant (iv)): it matches like a refusal, and the write settles as
   * unanswered, not refused. The client's durable-failure router
   * (`ClientConfig.debounceExecutor`) reaches this table's registry through
   * its `DurableRegistries.report`, with every other registry of the client;
   * this door is the table's own, for a table driven without a client.
   */
  reportDurableFailure(failure: DebouncedRequests.Failure): boolean {
    return this.#durable.report(failure);
  }

  /**
   * Apply an optimistic event edit. For a **canonical** row this is a
   * SINGLE write to the rows-engine `canonicalCells` store: the month view
   * reads canonicalCells directly, and the forward bridge
   * (`SheetDataSource.attachCanonicalStore`) fans the same write into this
   * row's `RowCellCache` (week view + this table's index). One write reaches
   * both views — no second store to mirror and forget (Move 3: the
   * dual-write, and its ghost/zombie class, is gone). A non-canonical row
   * has no canonical projection, so it writes the `RowCellCache` directly.
   *
   * The cell lane is a function of the event VALUE (deconfliated): all-day
   * (`startDate`/`endDate`) → semantic date lane; timed → timepoint lane.
   * Both lanes are id-keyed, so a same-lane same-date edit overwrites in
   * place; a cross-lane all-day⇄timed toggle or a date move clears the old
   * cell first, or the id would live in two lanes / at two dates.
   */
  applyOptimisticEdit(
    state: EventsRowState,
    value: CalendarEvent,
    id: EventId,
    prev: Option<CalendarEvent>,
  ): void {
    if (!state.rh) {
      this.removeLocal(id);
      this.ingest(state.rowLocalId, value, null);
      return;
    }
    const key = state.rh.lookupKey;
    if (key && isCanonicalKey(key)) {
      this.#writeCanonical(key, value, id, prev, null);
      return;
    }
    // Non-canonical row: write the RowCellCache directly, lane-correctly.
    if (
      prev != null &&
      CellLane.temporalKindOfEvent(prev) !== CellLane.temporalKindOfEvent(value)
    ) {
      const prevDate = NaiveDate.Partial.fromDate(EventsTable.#cellNaiveDate(prev));
      const prevAddr = state.rh.addressOfExisting(CellId.of(id), prevDate);
      if (prevAddr) state.rh.removeCell(prevAddr);
    }
    if (CellLane.temporalKindOfEvent(value) === "timepoint") {
      state.rh.replaceCustomCell(
        CellId.of(id),
        EventsTable.#cellDtrOf(value),
        CellId.of(id),
        value,
      );
    } else {
      state.rh.applyExternalEdit(
        EventsTable.#cellDateOf(value),
        value,
        id,
      );
    }
  }

  /** Optimistic delete — removes the event's cell. A canonical row clears
   *  via `canonicalCells` (the bridge fans the clear to the RowCellCache); a
   *  non-canonical row clears the RowCellCache directly at the resolved
   *  address. `prev` is the event being removed (it locates + lanes the
   *  cell). */
  applyOptimisticDelete(state: EventsRowState, prev: CalendarEvent, id: EventId): void {
    if (!state.rh) {
      this.#removeAndEmit(id);
      return;
    }
    // Clear the RowCellCache cell at its resolved lane. `addressOfExisting`
    // handles `custom` (remove by id), `multi` (id-keyed), and `standard`
    // (date cell, only on an id match so a same-date neighbor is never wiped)
    // — including a cell stranded in the standard store by a single→multi
    // transition, which the bridge's id-keyed clear can't reach. An absent
    // cell (`addr == null`) is a no-op.
    const addr = state.rh.addressOfExisting(CellId.of(id), EventsTable.#cellDateOf(prev));
    if (addr) state.rh.removeCell(addr);
    // A canonical row also projects to `canonicalCells` (month view); clear it
    // there so the projection drops the cell. The forward bridge mirrors this
    // back to the RowCellCache — a no-op now the rh cell is already gone.
    const key = state.rh.lookupKey;
    if (key && isCanonicalKey(key)) {
      this.#writeCanonical(key, null, id, prev, null);
    }
  }

  /**
   * Write (or clear, when `value == null`) an event's cell in the rows-
   * engine `canonicalCells` store, lane-correctly: all-day → semantic date
   * lane (`set`/`clear`); timed → timepoint lane
   * (`setCustom`/`removeCustom`), keyed by the canonical row `key`. The
   * forward bridge (`SheetDataSource.attachCanonicalStore`) fans every
   * write back into the RowCellCache, so this SINGLE write reaches the month
   * view (reads canonicalCells) AND the week view + this table's index (rh,
   * via the bridge) — there is no second store to mirror and forget.
   *
   * `prev` locates the cell to clear when the write removes it, flips its
   * lane (all-day⇄timed toggle), or moves its date — so an id can never
   * survive in two lanes or at two dates. A same-lane same-date edit needs
   * no clear: the id-keyed `set`/`setCustom` overwrites in place.
   *
   * `emit` fires a synchronous `ViewportsInvalidated` for the move/create
   * anti-flicker — the month layout must re-seed its spans in the same
   * frame the drag ghost clears, before the async row-open. Returns true
   * when a write happened (canonical key + store wired).
   */
  #writeCanonical(
    key: string,
    value: Option<CalendarEvent>,
    id: string,
    prev: Option<CalendarEvent>,
    /** Emit a synchronous `ViewportsInvalidated` stamped with this actor;
     *  `null`: emit nothing (the caller's batch emits). */
    emitAs: Option<Actor>,
  ): boolean {
    if (!isCanonicalKey(key)) return false;
    const store = this.#canonicalCells();

    if (prev != null) {
      const clearPrev = value == null ||
        CellLane.temporalKindOfEvent(prev) !==
          CellLane.temporalKindOfEvent(value) ||
        !EventsTable.#cellNaiveDate(prev).equals(
          EventsTable.#cellNaiveDate(value),
        );
      if (clearPrev) EventsTable.#clearCanonicalLane(store, key, prev, id);
    }

    if (value != null) {
      if (CellLane.temporalKindOfEvent(value) === "timepoint") {
        store.setCustom(
          key,
          { id: CellId.of(id), dtr: EventsTable.#cellDtrOf(value), value, style: null },
          null,
        );
      } else {
        store.set(key, EventsTable.#cellDateOf(value), value, CellId.of(id), null, null);
      }
    }

    if (emitAs != null) this.invalidation.emit(new ViewportsInvalidated(emitAs));
    return true;
  }

  /**
   * Clear an event's canonical cell from BOTH lanes by id — the timepoint
   * (custom) lane and the semantic date lane. A cell occupies exactly one lane,
   * but routing the clear solely by `temporalKindOfEvent` targets the WRONG lane
   * when a timed master was created/hydrated into the semantic (date) lane (lane
   * drift), so the clear silently misses it: `canonicalCells` keeps the master,
   * no `ColumnDataChanged` fires, and the cell-backed `EventsFacade` never
   * re-collects — leaving a deleted recurring series' synthetic occurrences
   * painted until reload. Clearing both lanes by id is safe (the lane the cell
   * is NOT in is a no-op removal) and self-healing; `clear` is emit-always, so
   * the facade is notified and drops the master (and its expanded occurrences)
   * regardless of which lane actually held it. Reaches the right address without
   * a second source of truth (Move-2 tail; the structural collapse is Move 3).
   */
  static #clearCanonicalLane(
    store: CanonicalCellSink,
    key: string,
    ev: CalendarEvent,
    id: string,
  ): void {
    store.removeCustom(key, id, null);
    store.clear(key, EventsTable.#cellDateOf(ev), CellId.of(id), null);
  }

  /**
   * Synchronously promote a created/moved event into `canonicalCells`
   * (month view) — and, via the forward bridge, the RowCellCache (week view) —
   * keyed by the per-calendar lookupKey derived from the event itself, so
   * **no source-row open is required**, then emit a synchronous
   * invalidation so the layout re-seeds its spans *before* the gesture
   * clears its drag ghost (otherwise the original repaints at the old spot
   * for the ~100ms the async row-open takes — the move "flicker").
   *
   * The async write path (`EventOps.update`/`create`) still runs afterward
   * and is idempotent here — it re-writes the same id-keyed cell (value-
   * identical, short-circuited by the facade dirty-hash) and the clear-old
   * is a no-op once already cleared.
   *
   * Returns true when a canonical write happened. The invalidation is
   * stamped with `actor`, the author of the write being projected.
   */
  materializeMoveCanonical(
    authId: AuthId,
    calendarId: CalendarId,
    value: CalendarEvent,
    id: string,
    prev: Option<CalendarEvent>,
    actor: Actor,
  ): boolean {
    return this.#writeCanonical(
      this.eventsLookupKeyFor(authId, calendarId, "materializeMoveCanonical"),
      value,
      id,
      prev,
      actor,
    );
  }

  /**
   * Write (or clear, when `value == null`) an event's optimistic cell in the
   * shared `canonicalCells` store **by canonical row key**, without requiring
   * the row to be attached to this table. The store is keyed by lookupKey — no
   * `RowCellCache`/`EventsRowState` is needed — so a create/edit onto a
   * per-calendar `:~google:…:events` source row that isn't attached here (only
   * the `:~lona:` aggregator is) still reaches the month view (reads
   * canonicalCells) and, via the forward bridge, the week view. This is the
   * projector sink's path for unattached canonical rows; it closes the Move-3
   * create-live gate.
   *
   * `emit = false`: unlike `materializeMoveCanonical` (synchronous anti-flicker
   * emit on drag), the projector fires invalidation for the whole write batch
   * via `emitForWrites`, so this MUST NOT double-emit. No-ops (returns false)
   * for non-canonical keys or before the store is wired.
   */
  writeCanonicalForKey(
    key: string,
    value: Option<CalendarEvent>,
    id: string,
    prev: Option<CalendarEvent>,
  ): boolean {
    return this.#writeCanonical(key, value, id, prev, null);
  }

  /**
   * Keep the global id→item authority (`itemFor` / `peekInRange`) in sync with
   * an UNATTACHED canonical row's optimistic write, so a freshly-created event
   * that renders from `canonicalCells` is ALSO *resolvable* by id (right-click /
   * details edit·delete·move via `RowEvents.requireEvent`) and visible to
   * table-only surfaces (home "today", pending). The table index is otherwise
   * populated only for ATTACHED rows (via the bridge→RowCellCache rebuild hook), so
   * an event created on a not-yet-attached per-calendar source row rendered but
   * could not be resolved — the gap `RowEvents.peekInTable`'s reader fallback used to
   * paper over. Move-3: this makes `itemFor` authoritative for everything that
   * renders, so that fallback becomes dead.
   *
   * Tagged under the SOURCE row's localId (resolved from `key` via the same
   * shared registry `attachRow` uses), so a later source-row attach
   * (`populateRow`→`clearRow`) evicts this id before re-ingesting the
   * server-canonical event — an idempotent handoff, no duplicate. `removeLocal`
   * first makes the edit case an in-place upsert (no AVL double); a clear
   * (`value == null`) just evicts. Emit-free: the projector's `emitForWrites`
   * fires invalidation for the batch.
   *
   * No double-render: the calendar reads `canonicalCells` first (month source;
   * week tier-1 of an id-deduped union), reaching the table only as a
   * short-circuited fallback tier — so this index entry surfaces a fresh event
   * only on surfaces `canonicalCells` does not already cover.
   */
  ingestCanonicalForKey(key: string, value: Option<CalendarEvent>, id: EventId): void {
    if (!isCanonicalKey(key)) return;
    this.removeLocal(id);
    if (value != null) {
      this.ingest(this.#ids.resolveFromRawId(key), value, null);
    }
  }

  /** The `NaiveDate` an event's cell is keyed at (its start). */
  static #cellNaiveDate(ev: CalendarEvent): NaiveDate {
    return ev.startDate != null ? ev.startDate : ev.start.date;
  }

  /** The cell's real temporal coordinate for an event write — replaces the
   *  former fabricated `new Date()` placeholder. A timed event with a server id
   *  uses its start instant (RFC3339 `Point`); a full-day event, or any id-less
   *  write (create / copyTo — the connector rejects a `Point` cell without an
   *  id, cardinality contract `tento-lona-connector/src/cell.rs:73`), buckets by
   *  the event's real day as a semantic `Range` (`ymd-…`). The connector derives
   *  an event's placement from the payload, so this is the cell's storage key,
   *  not the event's authority — but it must still be the event's real date,
   *  never "now". `key` says which the write may use: `"point"` for a write
   *  that carries the event's id (a timed event keys at its start instant),
   *  `"day"` for an id-less one (always the day bucket). */
  static eventCellDate(
    startDate: Option<NaiveDate>,
    start: Option<DateTime<Utc>>,
    key: EventsTable.CellDateKey,
  ): SemanticTime {
    if (startDate != null) {
      return SemanticTime.fromPartialDate(NaiveDate.Partial.fromDate(startDate));
    }
    if (start != null) {
      return key === "point"
        ? SemanticTime.fromDateTime(start)
        : SemanticTime.fromPartialDate(NaiveDate.Partial.fromDate(start.date));
    }
    throw new Error(
      "EventsTable.eventCellDate: event has neither startDate nor start",
    );
  }

  /** The UTC datetime range a custom-store cell spans for this event
   *  (mirrors how `insertOptimistic` derives times for all-day events). */
  static #cellDtrOf(ev: CalendarEvent): DateTime.Range<Utc> {
    if (ev.startDate != null && ev.endDate != null) {
      return new DateTime.Range(
        new DateTime(ev.startDate.withTime(), Utc),
        new DateTime(ev.endDate.withTime(), Utc),
      );
    }
    return new DateTime.Range(ev.start, ev.end);
  }

  static #cellDateOf(ev: CalendarEvent): NaiveDate.Partial {
    return NaiveDate.Partial.fromDate(EventsTable.#cellNaiveDate(ev));
  }

  // ── Row attachment ─────────────────────────────────────────────

  /**
   * The SERVER-RENDERED events-row lookupKey for a calendar, resolved by
   * `(authId, calendarId)` through the loaded calendar list. The server
   * renders each calendar's key with its provider prefix (`:~google:` /
   * `:~outlook:`) AND primary normalization already applied, so no write
   * path assumes a provider.
   *
   * Throws when the calendar isn't in the loaded list. A constructed
   * `:~google:…` fallback would silently target a nonexistent row for any
   * non-google calendar (an outlook calendar's real key is
   * `:~outlook:<authId>:<calendarId>:events`) — the "synced but invisible"
   * class of bug. There is no provider-hardcoded fallback, by design.
   */
  eventsLookupKeyFor(authId: AuthId, calendarId: CalendarId, op: string): RowNetworkKey {
    const found = this.#calendars()?.peek([authId, calendarId]);
    if (found) return RowNetworkKey.fromWire(found[1].eventsLookupKey);
    throw new Error(
      `EventsTable.eventsLookupKeyFor(${op}): calendar ${authId}:${calendarId} ` +
        `not in the loaded calendar list — cannot resolve its events-row key ` +
        `(no provider-hardcoded fallback)`,
    );
  }

  /** Match list for event-bearing row types — used by the
   *  RowsClient dispatch to decide whether to call `attachRow`. */
  static readonly eventBearingRowTypes: readonly string[] = [
    "events",
    "calendar-list",
    "calendar-events",
    "alias.calendar-list",
    "alias.calendar",
  ];

  /** Register an event-bearing `RowCellCache` with the table. Sets
   *  up the rebuild hook so cell updates ingest into the global
   *  storage tagged with this row's `rowLocalId`. For aggregator
   *  rows (alias.calendar / calendar-list), configures the
   *  metaSourcesLoader fan-out that triggers loadRange on
   *  constituent source rows. Idempotent per rh. */
  attachRow(
    rh: RowCellCache,
    opts: { rowLocalId: RowLocalId; isAggregator: boolean },
  ): EventsRowState {
    const existing = this.#rowStateByHandle.get(rh);
    if (existing) return existing;
    const state: EventsRowState = {
      rowLocalId: opts.rowLocalId,
      get rowKey() { return RowNetworkKey.of(opts.rowLocalId); },
      rh,
      metaSourcesLoader: null,
    };
    this.#rowStateById.set(opts.rowLocalId, state);
    this.#rowStateByHandle.set(rh, state);
    EventsTable.#tableByRowHandle.set(rh, this);

    if (opts.isAggregator) {
      const open = this.#openRowHandle;
      let init: Option<Promise<void>> = null;
      state.metaSourcesLoader = async (range) => {
        // A failed calendar-list read rejects this load and is not
        // memoized: the next range retries it rather than fanning out to
        // an empty source list for the rest of the session.
        const loading = init ??= (async () => {
          const lists = await SdkRequestError.checkRequest(
            "calendars",
            "list",
            "GET",
            "/api/calendars",
            () => this.api.getResult("/api/calendars"),
          );
          for (const list of lists) {
            for (const calendar of list.calendars) {
              // The server renders each calendar's events lookup key
              // (provider prefix + primary normalization) — the authoritative
              // source. No client-side construction, which would assume a
              // provider.
              const sourceRowKey = RowNetworkKey.fromWire(calendar.eventsLookupKey);
              const sourceRh = await open(sourceRowKey);
              Tracing.log("events-agg/open-source", {
                sourceRowKey,
                opened: sourceRh != null,
              });
              // Every calendar's source row joins the fan-out: a row
              // `RowsClient` opened is attached already; one only this
              // opener reached (a reserved-key row no `rows.open` touched)
              // is attached here, so `loadRange` below reaches it — the
              // load is complete, never "the rows someone attached".
              if (sourceRh != null && this.getRowState(sourceRh) == null) {
                this.attachRow(sourceRh, { rowLocalId: this.#ids.resolveFromRawId(sourceRowKey), isAggregator: false });
              }
            }
          }
        })();
        try {
          await loading;
        } catch (err) {
          if (init === loading) init = null;
          throw err;
        }
        if (!range) return;
        for (const sourceKey of this.#metaSourceKeys()) {
          const sourceState = this.getRowStateByKey(RowNetworkKey.fromWire(sourceKey));
          if (sourceState) await this.ensureLoaded(sourceState, range);
        }
      };
    }

    // Repopulate the row's state now and after every rebuild of its cells.
    // Carried to forks: a sheet-scoped fork of this cache feeds the same row
    // state from its own cells, as the index factory this replaced did
    // (tests/events-table-fork pins it). The row state lives as long as the
    // table (no detach path), so the remover is not kept.
    // Change-scoped (`onCellsChangedCarried`): a
    // change that names its days re-reads only those days' cells; a change
    // of any day (registration, a whole-row read, a fork) re-reads the row.
    // A change of another cache than the one the slice was last populated
    // from (a sheet-scoped fork, carried) replaces the slice whole, as the
    // rebuild hook always did (tests/events-table-fork).
    rh.onCellsChangedCarried((handle, change) => {
      const range = change.range;
      const sameSource = this.#populatedFrom.get(state.rowLocalId) === handle;
      this.#populatedFrom.set(state.rowLocalId, handle);
      const scoped = range != null && sameSource && !this.#hasUnanchored(state.rowLocalId) &&
        !this.#multiPlaced.has(state.rowLocalId) &&
        this.#populateFromCells(
          state,
          range,
          change.cellsIn(range),
          handle.getAllTimepointsInRange(range.withTime().withTz(Utc)),
          (id, days) =>
            [...change.cellsIn(days), ...handle.getAllTimepointsInRange(days.withTime().withTz(Utc))]
              .some((cell) => cellValueAsEvent(cell.value)?.id === id),
        );
      if (!scoped) this.#populateFromCells(state, null, change.cells(), TaggedCells.customs(handle.allCells()));
      // Recurrence expansion is the sheets-side `EventsIndex`'s job (the single
      // expander — P4.2). The SDK index holds only real cells (masters + Google
      // override instances); it no longer mints synthetic occurrences.
    });

    return state;
  }

  /** Lookup the row state for a `RowCellCache`. Returns `null` if the
   *  row hasn't been attached. */
  getRowState(rh: RowCellCache): Option<EventsRowState> {
    return this.#rowStateByHandle.get(rh) ?? null;
  }

  /** Lookup an attached row by physical id or canonical lookup key.
   *  A source row can have a persisted physical id while the provider's
   *  event route is its `:~` lookup key; native move reconciliation needs
   *  its RowCellCache even if the canonical store never held that cell. */
  getRowStateByKey(rowKey: RowNetworkKey): Option<EventsRowState> {
    const direct = this.#rowStateById.get(this.#ids.resolveFromRawId(rowKey));
    if (direct != null) return direct;
    for (const state of this.#rowStateById.values()) {
      if (state.rh.lookupKey === rowKey) return state;
    }
    return null;
  }

  /** Source-row rowKeys reachable from any attached aggregator. */
  #metaSourceKeys(): readonly string[] {
    const out: string[] = [];
    for (const state of this.#rowStateById.values()) {
      // A source row is one that doesn't itself have a
      // metaSourcesLoader (only aggregators do).
      if (!state.metaSourcesLoader) out.push(state.rowKey);
    }
    return out;
  }

  // ── Populate / loadRange ───────────────────────────────────────

  /**
   * The rebuild hook's populate, from the row's cells: with `range` null the
   * row's slice becomes exactly the events of `dated` + `customs` (as
   * `populateRow`); with a `range`, only the events whose cells lie in those
   * days are reconciled — a held event anchored outside them is untouched —
   * and `dated` / `customs` are the cells in `range`. Each event remembers
   * the days of the cell it came from (`#anchors`); an event ingested any
   * other way (a write's projection) has none, and a row holding one takes
   * the whole-row path until a populate anchors it.
   */
  #populateFromCells(
    state: EventsRowState,
    range: Option<NaiveDate.Range>,
    dated: readonly CellEntry[],
    customs: readonly TimepointCellEntry[],
    /** Whether the row still holds event `id` in a cell on `days` (a scoped change's look outside its range). */
    holdsAt: Option<(id: EventId, days: NaiveDate.Range) => boolean> = null,
  ): boolean {
    const rowId = state.rowLocalId;
    const next = new Map<string, { ev: CalendarEvent; lo: number; hi: number }>();
    // Ids held in more than one cell of the row (a dated and a timepoint copy, or two dated cells): an
    // anchor names one of them, so a scoped change of one copy cannot tell whether the event stays
    // Such a row takes the whole-row path until a whole-row read finds none.
    const twice = new Set<EventId>();
    const place = (ev: CalendarEvent, lo: number, hi: number) => {
      if (next.has(ev.id)) twice.add(ev.id);
      next.set(ev.id, { ev, lo, hi });
    };
    for (const cell of dated) {
      const ev = cellValueAsEvent(cell.value);
      if (ev == null) continue;
      const days = cell.date.toRange();
      place(ev, days.start.dse, days.end.dse);
    }
    for (const cell of customs) {
      const ev = cellValueAsEvent(cell.value);
      if (ev == null) continue;
      const day = cell.dtr.start.toUtc().ndt.date.dse;
      place(ev, day, day + 1);
    }
    if (range != null) {
      // A scoped change refuses (answers `false`, nothing changed) when an id it holds is held elsewhere
      // too: twice in the range, or still in a cell on the days it is anchored to outside the range (an
      // event that moved here no longer is).
      if (twice.size > 0) return false;
      for (const { ev } of next.values()) {
        const at = this.#anchors.get(ev.id);
        if (at == null || at.row !== rowId || (at.lo < range.end.dse && range.start.dse < at.hi)) continue;
        const days = new NaiveDate.Range(range.start.addDays(at.lo - range.start.dse), range.start.addDays(at.hi - range.start.dse));
        if (holdsAt?.(ev.id, days) ?? true) return false;
      }
    } else if (twice.size > 0) {
      this.#multiPlaced.add(rowId);
    } else {
      this.#multiPlaced.delete(rowId);
    }
    const held = this.#idsByRow.get(rowId);
    if (held) {
      // A change of some days visits the events anchored on them (the scoped path runs only when every
      // event the row holds is anchored), not every event of the row: an edit of one day of a 10k-event
      // history read 10k anchors before.
      const visit = range == null ? [...held] : this.#anchors.overlapping(rowId, range.start.dse, range.end.dse, held);
      EventsTableWork.populateVisits += visit.length;
      for (const id of visit) {
        const fresh = next.get(id);
        // An event anchored on a wider cell that only overlaps the range (a week cell under a one-day
        // change) is not among the range's cells: it stays while that cell still holds it.
        const at = fresh == null && range != null ? this.#anchors.get(id) : null;
        if (at != null && range != null && (at.lo < range.start.dse || range.end.dse < at.hi)) {
          const days = new NaiveDate.Range(range.start.addDays(at.lo - range.start.dse), range.start.addDays(at.hi - range.start.dse));
          if (holdsAt?.(id, days) ?? false) continue;
        }
        if (fresh != null && this.rawById.get(id) === fresh.ev) {
          this.#anchors.set(rowId, id, fresh.lo, fresh.hi);
          this.#unanchoredByRow.get(rowId)?.delete(id);
          next.delete(id); // unchanged: kept as it is
          continue;
        }
        this.#removeId(id);
        this.#rowByEvent.delete(id);
        held.delete(id);
      }
      if (held.size === 0) this.#idsByRow.delete(rowId);
    }
    for (const { ev, lo, hi } of next.values()) {
      EventsTableWork.populateIngests++;
      this.ingest(rowId, ev, null);
      this.#anchors.set(rowId, ev.id, lo, hi);
      this.#unanchoredByRow.get(rowId)?.delete(ev.id);
    }
    this.invalidation.emit(new ViewportsInvalidated(EventsTable.ACTOR));
    return true;
  }

  /** Rows a whole-row read found holding one event id in more than one cell (`#populateFromCells`). */
  readonly #multiPlaced = new Set<RowLocalId>();

  /** The days of the cell each populated event came from (`#populateFromCells`). */
  readonly #anchors = new EventAnchors();
  /** Per row, the cache its slice was last populated from (`attachRow`'s hook). */
  readonly #populatedFrom = new Map<RowLocalId, RowCellCache>();
  /** Per row, the events it holds without an anchor (ingested other than by a populate). */
  readonly #unanchoredByRow = new Map<RowLocalId, Set<EventId>>();

  #hasUnanchored(rowId: RowLocalId): boolean {
    return (this.#unanchoredByRow.get(rowId)?.size ?? 0) > 0;
  }

  /**
   * Makes this row's slice hold exactly `events` + `customEvents` (the
   * rebuild hook now goes through `#populateFromCells`, which also scopes a
   * change to its days): the fresh cell set (custom events after date
   * events; a later event of an id wins). Incremental: an event this row
   * already holds as the same object keeps its item and tree node; only
   * events that are new, changed (another object) or gone are removed and
   * ingested (`EventsTableWork.populateIngests`), so an edit of one event of
   * a long history re-indexes one item, not the history.
   */
  populateRow(
    state: EventsRowState,
    events: readonly CalendarEvent[],
    customEvents: Option<readonly CalendarEvent[]>,
  ): void {
    const rowId = state.rowLocalId;
    const next = new Map<string, CalendarEvent>();
    for (const ev of events) next.set(ev.id, ev);
    if (customEvents) for (const ev of customEvents) next.set(ev.id, ev);
    const held = this.#idsByRow.get(rowId);
    if (held) {
      for (const id of [...held]) {
        const ev = next.get(id);
        if (ev != null && this.rawById.get(id) === ev) {
          next.delete(id); // unchanged: kept as it is
          continue;
        }
        this.#removeId(id);
        this.#rowByEvent.delete(id);
        held.delete(id);
      }
      if (held.size === 0) this.#idsByRow.delete(rowId);
    }
    for (const ev of next.values()) {
      EventsTableWork.populateIngests++;
      this.ingest(rowId, ev, null);
    }
    // Repaint on ingest. `populateRow` runs on every `_rebuildAllIndices`,
    // which includes the server-fetch / sync ingest path — and that path (unlike
    // the mutation ops, which emit their own invalidation) fired nothing. So a
    // late-arriving initial or backfill sync populated the store without ever
    // triggering the layout plugins' `"calendars.events"` subscribers, leaving
    // the first paint empty until a manual refresh. Emitting here makes freshly
    // ingested events paint immediately. Mutation-triggered rebuilds also pass
    // through here and thus double-emit, but that's a harmless idempotent extra
    // rebind (discrete — once per op, not per drag frame). Loop-safe: the
    // subscribers (`calendar-month`/`timeline-column`) rebind from cache without
    // re-fetching, so nothing re-enters `_rebuildAllIndices`.
    this.invalidation.emit(new ViewportsInvalidated(EventsTable.ACTOR));
  }

  /** Drive a cell fetch on the row. For aggregator rows, fans out
   *  to all constituent source rows via metaSourcesLoader. */
  async ensureLoaded(
    state: EventsRowState,
    range: DateTime.Range<Utc>,
  ): Promise<void> {
    if (state.metaSourcesLoader) {
      await state.metaSourcesLoader(range);
      return;
    }
    await state.rh.fetch(range);
  }

  // ── Optimistic-id generation ───────────────────────────────────

  /**
   * A placeholder id for an optimistic insert (`optimistic-event-N`) that no
   * real event id collides with. The counter is this table's, and a client
   * has one table, so the ids are the client's: two clients in one process
   * each count from 1, and nothing process-wide is written.
   */
  generateOptimisticId(): EventId {
    return EventId.of(`optimistic-event-${++this.#optimisticIds}`);
  }
  #optimisticIds = 0;

  // ── Storage mutators ───────────────────────────────────────────

  /**
   * Ingest a wire-shape `Event` and tag it with `rowLocalId`.
   * `recurrenceInfo` lets synthetic recurrence instances carry their
   * `RecurrenceInfo.Instance` so the UI recognizes them as occurrences of
   * a master (right-click delete/edit, hydration). Real cells pass `null`
   * → the EventItem derives Parent/Instance from its own id.
   */
  ingest(
    rowLocalId: RowLocalId,
    ev: CalendarEvent,
    recurrenceInfo: Option<RecurrenceInfo>,
  ): void {
    // An id is one item in exactly one lane and one backing-row slice.
    // Rebuilds and canonical projections share this atomic upsert.
    this.removeLocal(ev.id);
    this.rawById.set(ev.id, ev);
    this.#trackRowId(rowLocalId, ev.id);
    // Unanchored until a populate says which cell it came from.
    let unanchored = this.#unanchoredByRow.get(rowLocalId);
    if (unanchored == null) this.#unanchoredByRow.set(rowLocalId, unanchored = new Set());
    unanchored.add(ev.id);

    if (ev.startDate != null && ev.endDate != null) {
      const item = new EventItemAllDay(
        ev,
        recurrenceInfo ?? RecurrenceInfo.ofEvent(ev),
        EventOrigin.backingRow(rowLocalId),
      );
      this.allDayBaseIndex.set(ev.id, item);
      this.allDayIndex.insert(new AVLTree.Node.Many([item]));
    } else {
      const item = new EventItemTimed(
        ev,
        undefined,
        recurrenceInfo ?? RecurrenceInfo.ofEvent(ev),
        EventOrigin.backingRow(rowLocalId),
      );
      this.baseIndex.set(ev.id, item);
      this.rangeIndex.insert(new AVLTree.Node.Many([item]));
    }
  }

  /** Remove every event tagged with `rowKey`. */
  clearRow(rowKey: RowNetworkKey): void {
    this.#clearRow(this.#ids.resolveFromRawId(rowKey));
  }

  #clearRow(rowId: RowLocalId): void {
    const ids = this.#idsByRow.get(rowId);
    if (!ids) return;
    for (const id of ids) {
      this.#removeId(id);
      this.#rowByEvent.delete(id);
    }
    this.#idsByRow.delete(rowId);
  }

  /** Remove an event from local state. Returns the removed item +
   *  its kind so the caller can target an appropriate invalidation. */
  removeLocal(id: EventId): Option<
    | { kind: "partial"; item: EventItemTimed }
    | { kind: "full"; item: EventItemAllDay }
  > {
    const removed = this.#removeId(id);
    this.#untrackId(id);
    return removed;
  }

  /** Used by `applyOptimisticDelete`: removes the
   *  event locally and emits the appropriate invalidation event.
   *
   *  Also clears the cell from `canonicalCells` (the shared cross-
   *  sheet store) so optimistic placeholders disappear after
   *  `deleteLocal` — without this, the calendar-month renderer would
   *  keep rendering the optimistic span next to the real one once
   *  the server-canonical event is `materializeMoveCanonical`'d in. */
  #removeAndEmit(id: EventId): void {
    const raw = this.rawById.get(id);
    const removed = this.removeLocal(id);
    if (!removed) return;
    if (raw) {
      const store = this.#canonicalCells();
      // Resolve the SERVER-RENDERED events-row key (throws if the calendar
      // isn't loaded — never a constructed provider key).
      const key = this.eventsLookupKeyFor(
        raw.authId,
        raw.calendarId,
        "removeAndEmit",
      );
      // Lane-correct clear: a timed event lives in the timepoint lane, where
      // a semantic date-clear can't reach it (would strand a zombie span).
      if (isCanonicalKey(key)) EventsTable.#clearCanonicalLane(store, key, raw, id);
    }
    if (removed.kind === "full") {
      this.invalidation.emit(new ViewportsInvalidated(EventsTable.ACTOR));
    } else {
      this.invalidation.emit(
        new TimepointsChanged(
          EventsTable.ACTOR,
          EventsTable.#timepointRange([removed.item]),
        ),
      );
    }
  }

  /**
   * Remove a recurring master's synthetic occurrences from the in-memory index
   * (they carry `recurringEventId === masterId`) and forget its recurrence
   * generator + expanded-children set. Used by `EventOps.delete` when deleting a
   * whole series ("all" scope): the master-cell removal alone leaves the
   * already-expanded instances rendered until a reload, because they are
   * separate index entries at future timepoints (operator-reported 2026-06-19,
   * "deleting all of them doesn't update optimistically"). Returns the removed
   * items so the caller can emit a repaint over their range. Idempotent: when the
   * synchronous row rebuild already wiped the synthetics it just clears the
   * generator/expanded-children maps and returns `[]`.
   */
  removeRecurrenceChildren(
    state: EventsRowState,
    masterId: EventId,
  ): EventItem[] {
    const childIds: EventId[] = [];
    for (const [cid, raw] of this.rawById) {
      if (cid !== masterId && raw.recurringEventId === masterId) {
        childIds.push(cid);
      }
    }
    const removed: EventItem[] = [];
    for (const cid of childIds) {
      const r = this.removeLocal(cid);
      if (r) removed.push(r.item);
    }
    return removed;
  }

  // ── Invalidation emitters ──────────────────────────────────────

  /** Post-write invalidations for inserted `items`, through the table's sink/ACTOR. */
  #emitInsert(items: EventItem[]): void {
    const partial: EventItemTimed[] = [];
    let anyFull = false;
    for (const it of items) {
      if (it instanceof EventItemTimed) partial.push(it);
      else if (it instanceof EventItemAllDay) anyFull = true;
    }
    if (partial.length > 0) {
      this.invalidation.emit(
        new TimepointsChanged(
          EventsTable.ACTOR,
          EventsTable.#timepointRange(partial),
        ),
      );
    }
    if (anyFull) {
      this.invalidation.emit(new ViewportsInvalidated(EventsTable.ACTOR));
    }
  }

  /** Post-write invalidations for a `prev` → `next` move; `EventOps` reaches it through `emitEventUpdate`. */
  #emitUpdate(prev: EventItem, next: EventItem, actor: Actor): void {
    const partial: EventItemTimed[] = [];
    let full = false;
    for (const it of [prev, next]) {
      if (it instanceof EventItemTimed) partial.push(it);
      else if (it instanceof EventItemAllDay) full = true;
    }
    if (partial.length > 0) {
      this.invalidation.emit(
        new TimepointsChanged(actor, EventsTable.#timepointRange(partial)),
      );
    }
    if (full) {
      this.invalidation.emit(new ViewportsInvalidated(actor));
    }
  }


  #trackRowId(rowId: RowLocalId, id: EventId): void {
    let set = this.#idsByRow.get(rowId);
    if (!set) {
      set = new Set();
      this.#idsByRow.set(rowId, set);
    }
    set.add(id);
    this.#rowByEvent.set(id, rowId);
  }

  #untrackId(id: EventId): void {
    const rowId = this.#rowByEvent.get(id);
    if (rowId == null) return;
    const set = this.#idsByRow.get(rowId);
    if (set != null) {
      set.delete(id);
      if (set.size === 0) this.#idsByRow.delete(rowId);
    }
    this.#rowByEvent.delete(id);
  }

  #removeId(id: EventId): Option<
    | { kind: "partial"; item: EventItemTimed }
    | { kind: "full"; item: EventItemAllDay }
  > {
    this.rawById.delete(id);
    this.#anchors.delete(id);
    const owner = this.#rowByEvent.get(id);
    if (owner != null) this.#unanchoredByRow.get(owner)?.delete(id);
    const fullItem = this.allDayBaseIndex.get(id);
    if (fullItem) {
      this.allDayBaseIndex.delete(id);
      this.allDayIndex.delete(new AVLTree.Node.Many([fullItem]));
    }
    const partialItem = this.baseIndex.get(id);
    if (partialItem) {
      this.baseIndex.delete(id);
      this.rangeIndex.delete(new AVLTree.Node.Many([partialItem]));
    }
    return fullItem ? { kind: "full", item: fullItem }
      : partialItem ? { kind: "partial", item: partialItem } : null;
  }

  static #timepointRange(
    events: EventItemTimed[],
  ): { start: MsSinceEpoch; end: MsSinceEpoch } {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const event of events) {
      min = Math.min(min, event.time.mse.start);
      max = Math.max(max, event.time.mse.end);
      if (event.recurrenceInfo != null) {
        max = Number.MAX_SAFE_INTEGER;
      }
    }
    return { start: MsSinceEpoch.of(min), end: MsSinceEpoch.of(max) };
  }

  // ── Queries (optionally scoped by rowKey set) ──────────────────

  has(id: string): boolean {
    return this.baseIndex.has(id) || this.allDayBaseIndex.has(id);
  }

  /**
   * Resolve an event by id from the client-scoped index — the single id→item
   * authority (Move 2). Backs `RowEvents.requireEvent`. Returns null if absent.
   */
  itemFor(id: string): Option<EventItem> {
    return this.baseIndex.get(id) ?? this.allDayBaseIndex.get(id) ?? null;
  }

  // ── Overlay projector (SDK 2.0 Move-4) — the single optimistic writer ─────

  #overlayProjector: Option<EventOverlayProjector> = null;

  /** Lazily install the apply-mode overlay projector on the first mutation.
   *  `EventOps` supplies the constructor (with the lane sink + emit closures) so
   *  the mutation graph is not eagerly imported into this module — see the
   *  type-only import note above. See `event-overlay-projector.ts`. */
  ensureOverlayProjector(
    make: () => EventOverlayProjector,
  ): EventOverlayProjector {
    return (this.#overlayProjector ??= make());
  }

  /** The projector if it has been built, else null (stays null until the first
   *  mutation on this row). */
  peekOverlayProjector(): Option<EventOverlayProjector> {
    return this.#overlayProjector;
  }

  /** Fire the invalidations for a batch of projection writes — the partial-day
   *  timepoint range (over each write's prev + value) plus a viewport invalidate
   *  for any full-day change. The apply-mode projector's emit hook; replaces the
   *  gestures' explicit `emitInsert` / `emitUpdate` on the `on` path. Stamped
   *  with `actor`, the author of the op that caused the writes; `null` (a
   *  reaper expiry) stamps `EventsTable.ACTOR`. */
  emitForWrites(
    writes: readonly ProjectionWrite<EventItem, EventId>[],
    actor: Option<Actor>,
  ): void {
    const by = actor ?? EventsTable.ACTOR;
    const partial: EventItemTimed[] = [];
    let anyFull = false;
    for (const w of writes) {
      for (const it of [w.value, w.prev]) {
        if (it instanceof EventItemTimed) partial.push(it);
        else if (it instanceof EventItemAllDay) anyFull = true;
      }
    }
    if (partial.length > 0) {
      this.invalidation.emit(
        new TimepointsChanged(by, EventsTable.#timepointRange(partial)),
      );
    }
    if (anyFull) {
      this.invalidation.emit(new ViewportsInvalidated(by));
    }
  }

  /** The row's decoded events keyed by `EventItem.id` — the legacy
   *  authoritative snapshot the overlay shadow seeds base from and compares
   *  against. Pure read of the existing projection (no network). */
  rowSnapshot(rowKey: RowNetworkKey): ReadonlyMap<EventId, EventItem> {
    const out = new Map<EventId, EventItem>();
    for (const id of this.#idsByRow.get(this.#ids.resolveFromRawId(rowKey)) ?? []) {
      const item = this.itemFor(id);
      if (item) out.set(item.id, item);
    }
    return out;
  }

  /**
   * The timed events that belong to `dt`'s UTC day `[00:00, next 00:00)` —
   * lona's half-open day membership (`TimeMembership.select`, the query
   * `EventsIndex` answers with): an event starting at the next midnight is
   * not on this day, a zero-length event at this day's midnight is.
   */
  peekOnDate(
    dt: DateTime<Utc>,
    scope: Option<ReadonlySet<string>>,
  ): EventItemTimed[] {
    const startOfDay = DateTime.fromDse(dt.date.dse, Utc);
    const start = startOfDay.mse;
    const end = MsSinceEpoch.of(start + 86_400_000);
    const all = TimeMembership.select(this.rangeIndex.root ?? null, start, end);
    return scope ? all.filter((it) => scope.has(it.origin.backingRowId().toString())) : all;
  }

  /**
   * The timed events that belong to `range` — lona's half-open membership
   * (`TimeMembership.select`): an event ending exactly at the range's start
   * or starting exactly at its end is not in it, and a zero-length event is
   * in the one range containing its instant.
   */
  peekInRange(
    range: DateTime.Range<Utc>,
    scope: Option<ReadonlySet<string>>,
  ): EventItemTimed[] {
    const all = TimeMembership.select(this.rangeIndex.root ?? null, range.start.mse, range.end.mse);
    return scope ? all.filter((it) => scope.has(it.origin.backingRowId().toString())) : all;
  }

  peekAllDayInRange(
    range: NaiveDate.Range,
    scope: Option<ReadonlySet<string>>,
  ): EventItemAllDay[] {
    const all =
      this.allDayIndex.root?.findAllOverlapsInclusiveEnd({
        start: range.start.dse,
        end: range.end.dse - 1,
      }) ?? [];
    return scope ? all.filter((it) => scope.has(it.origin.backingRowId().toString())) : all;
  }

  peekAllDayEvents(
    scope: Option<ReadonlySet<string>>,
  ): EventItemAllDay[] {
    if (!scope) return [...this.allDayBaseIndex.values()];
    const out: EventItemAllDay[] = [];
    for (const item of this.allDayBaseIndex.values()) {
      if (scope.has(item.origin.backingRowId().toString())) out.push(item);
    }
    return out;
  }

  // ── Standalone seeding (null-state demos / tests) ───────────────

  /** Build a table pre-populated with a fixed set of events under
   *  a single synthetic row. No RowCellCache, no api/invalidation
   *  wiring — only reads work. Mutations throw: they need an attached
   *  rh, and reading `api` throws. */
  static seeded(events: readonly CalendarEvent[], rowLocalId: RowLocalId): EventsTable {
    const table = new EventsTable(
      // No API: `api` throws "EventsTable.seeded is read-only".
      null,
      InvalidationSink.NOOP,
      // A seeded table is read-only (every mutation path needs an attached
      // `rh` and throws), so nothing ever resolves through this registry —
      // it exists so the scope is OWNED rather than borrowed from a global.
      new RowLocalId.Registry(),
      async () => null,
      () => {
        throw new Error("EventsTable.seeded: a seeded table is read-only; it has no canonical store");
      },
      () => null,
      // Read-only: no durable write ever waits here; still a registry of an
      // owned set, like every other.
      new DurableRegistries().create(),
    );
    for (const ev of events) table.ingest(rowLocalId, ev, null);
    return table;
  }
}

export namespace EventsTable {
  /**
   * Which cell date an event write may key at (`EventsTable.eventCellDate`):
   * `"point"` — the write carries the event's id, so a timed event keys at its
   * start instant; `"day"` — an id-less write (create, copy), always the day
   * bucket (the connector rejects a `Point` cell without an id).
   */
  export type CellDateKey = "point" | "day";
}

/**
 * Narrow an array of cells to canonical `Event[]`. Boundary
 * tolerant of raw EventDb wire objects so one malformed row
 * metadata response doesn't break event indexing.
 */
/**
 * The days of the cell each populated event came from (`EventsTable.#populateFromCells`), and per row an index of
 * them by first day, so a change of some days finds the events anchored on them without a scan of the row. An
 * event spanning several days is found from its first day: a query looks back the row's widest span.
 *
 * A row's day index is built at its first scoped query, from the anchors, and kept up to date from then on: a
 * row's first populate (a whole-row read: every event anchored at once) records anchors only, so attaching a
 * row costs what it did before the index, and the index's one build is paid by the row's first one-day change.
 */
class EventAnchors {
  readonly #byId = new Map<EventId, { readonly row: RowLocalId; readonly lo: number; readonly hi: number }>();
  /** Per row whose index was built, its anchors by first day and the widest span among them. */
  readonly #byRow = new Map<RowLocalId, AnchorSlot>();

  /** `id`'s anchor (its row and days), or `null`. */
  get(id: EventId): Option<{ readonly row: RowLocalId; readonly lo: number; readonly hi: number }> {
    return this.#byId.get(id) ?? null;
  }

  set(row: RowLocalId, id: EventId, lo: number, hi: number): void {
    const at = this.#byId.get(id);
    if (at != null && at.row === row && at.lo === lo && at.hi === hi) return;
    if (at != null) this.#unindex(id, at);
    this.#byId.set(id, { row, lo, hi });
    const slot = this.#byRow.get(row);
    if (slot != null) EventAnchors.#index(slot, id, lo, hi);
  }

  delete(id: EventId): void {
    const at = this.#byId.get(id);
    if (at == null) return;
    this.#byId.delete(id);
    this.#unindex(id, at);
  }

  /**
   * The events of `row` whose anchor overlaps the days [start, end), as a list the caller may mutate under.
   * `held` is the row's events (`EventsTable.#idsByRow`): a row's index is built from them at its first query,
   * so the build reads that row's anchors only, not every row's.
   */
  overlapping(row: RowLocalId, start: number, end: number, held: Iterable<EventId>): EventId[] {
    const slot = this.#slot(row, held);
    const out: EventId[] = [];
    EventsTableWork.populateLookbackDays += end - (start - slot.widest + 1);
    for (let lo = start - slot.widest + 1; lo < end; lo++) {
      const ids = slot.byLo.get(lo);
      if (ids == null) continue;
      for (const id of ids) {
        const at = this.#byId.get(id);
        if (at != null && at.hi > start) out.push(id);
      }
    }
    return out;
  }

  /** `row`'s day index, built from its own events' anchors (`held`) at its first query. */
  #slot(row: RowLocalId, held: Iterable<EventId>): AnchorSlot {
    let slot = this.#byRow.get(row);
    if (slot != null) return slot;
    slot = { byLo: new Map(), spans: new Map(), widest: 1 };
    for (const id of held) {
      const at = this.#byId.get(id);
      if (at != null && at.row === row) EventAnchors.#index(slot, id, at.lo, at.hi);
    }
    this.#byRow.set(row, slot);
    return slot;
  }

  #unindex(id: EventId, at: { readonly row: RowLocalId; readonly lo: number; readonly hi: number }): void {
    const slot = this.#byRow.get(at.row);
    const ids = slot?.byLo.get(at.lo);
    if (slot == null || ids == null || !ids.delete(id)) return;
    if (ids.size === 0) slot.byLo.delete(at.lo);
    // The row's widest span shrinks when its last anchor of that width goes.
    const width = at.hi - at.lo;
    const count = (slot.spans.get(width) ?? 0) - 1;
    if (count > 0) {
      slot.spans.set(width, count);
      return;
    }
    slot.spans.delete(width);
    if (width >= slot.widest) slot.widest = Math.max(1, ...slot.spans.keys());
  }

  static #index(slot: AnchorSlot, id: EventId, lo: number, hi: number): void {
    let ids = slot.byLo.get(lo);
    if (ids == null) slot.byLo.set(lo, ids = new Set());
    ids.add(id);
    slot.spans.set(hi - lo, (slot.spans.get(hi - lo) ?? 0) + 1);
    slot.widest = Math.max(slot.widest, hi - lo);
  }

  /** The widest anchored span of `row`'s built index (1 when unbuilt or empty): for tests. */
  widestOf(row: RowLocalId): number {
    return this.#byRow.get(row)?.widest ?? 1;
  }
}

/** A row's day index (`EventAnchors`): its anchors by first day, the count of anchors per span width, and the widest span. */
type AnchorSlot = { readonly byLo: Map<number, Set<EventId>>; readonly spans: Map<number, number>; widest: number };

function cellsAsEvents(
  cells: readonly (CellEntry | TimepointCellEntry)[],
): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  for (const cell of cells) {
    const event = cellValueAsEvent(cell.value);
    if (event) out.push(event);
  }
  return out;
}

function cellValueAsEvent(value: Option<CellValue>): Option<CalendarEvent> {
  if (value == null) return null;
  if (typeof value !== "object" || Array.isArray(value)) return null;
  if (isDecodedEvent(value)) return value;
  // The cell's JSON form, its declared fields decoded. Tolerant like
  // `event.v1` (not strict): a cell value may be an optimistic event
  // without `etag`/`source`.
  const wire = Json.object(Json.wire(value));
  if (wire == null) return null;
  const decoded = WireDecode.partial(Network.EventCell.MODEL, wire);
  return eventDbToEvent({ ...decoded.undeclared, ...decoded.value }) ?? null;
}

/**
 * A cell value the row's `event.v1` decoder already produced (`start` /
 * `end` are `DateTime`s, not the wire's `st` / `et`). Re-running the wire
 * parse on it would drop it: the decoded shape has no `st` to read.
 */
function isDecodedEvent(value: CellValue): value is CalendarEvent {
  return typeof value === "object" && value != null && "id" in value &&
    typeof value.id === "string" && "start" in value &&
    value.start instanceof DateTime && "end" in value &&
    value.end instanceof DateTime;
}

/**
 * A read-only events table over `events`, owned by its own registry — what a
 * null state or a preview renders when no row is attached. Returns the
 * `EventsTableReads` reads only: the live index (`EventsTable`, with its
 * writers, API and durable hand-offs) is SDK-internal and reached through
 * `EventsClient` and `RowCellCache`, never built by callers.
 */
export function seededEventsTable(events: readonly CalendarEvent[], rowLocalId: RowLocalId): EventsTableReads {
  return EventsTable.seeded(events, rowLocalId);
}
